import { useCallback, useEffect, useState } from "react";
import { MultiClientReviewProjection } from "@/types";
import { getPartnerAwareApiUrl, httpClient } from "@/providers/dataProvider/dataProvider";

interface UseMultiClientReviewProjectionProps {
  documentId: string | undefined;
  /** Only fire when enabled (e.g. the document is multi-client and has records). */
  enabled: boolean;
}

interface UseMultiClientReviewProjectionReturn {
  projection: MultiClientReviewProjection | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Fetches the multi-client ("book of business") forecast from
 * GET /documents/{id}/review-projection (#5321 / ADR-057; GET since #7433 so
 * documents:show is enough — see useReviewProjection).
 *
 * Same endpoint as the single-client useReviewProjection, but for a multi-client
 * document the backend returns the nested { clients: [...] } shape and requires
 * NO client_id_ref (each row resolves its own client within the document's
 * partner). Calls httpClient directly so auth + partner headers ride along.
 *
 * The endpoint is non-destructive; refetch is safe.
 */
export const useMultiClientReviewProjection = ({
  documentId,
  enabled,
}: UseMultiClientReviewProjectionProps): UseMultiClientReviewProjectionReturn => {
  const [projection, setProjection] = useState<MultiClientReviewProjection | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled || !documentId) {
      setProjection(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    // No client_id_ref: a multi-client projection is not scoped to a single client.
    const url = `${getPartnerAwareApiUrl()}/documents/${documentId}/review-projection`;
    httpClient(url, { method: "GET" })
      .then(({ json }: { json: unknown }) => {
        if (cancelled) return;
        const payload = json as Partial<MultiClientReviewProjection> | undefined;
        if (!payload || !Array.isArray(payload.clients)) {
          console.error(
            "Multi-client projection endpoint returned an unexpected response shape " +
              "(expected { clients: [...] }). Is the document flagged multi-client and the " +
              "mgmt-api running with #5319?",
            json
          );
          setError("Projection endpoint returned an unexpected response shape.");
          setProjection(null);
          return;
        }
        setProjection(payload as MultiClientReviewProjection);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("Multi-client review projection failed:", err);
        setError(err instanceof Error ? err.message : "Failed to load projection");
        setProjection(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentId, enabled, refreshKey]);

  return { projection, isLoading, error, refetch };
};
